import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
  Textarea,
} from '@data-delivery/d3s';
import * as ipaddr from 'ipaddr.js';
import { FileTextIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type { DropEvent, FileRejection } from 'react-dropzone';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isStrictIPorCIDR(input: string): boolean {
  try {
    if (input.includes('/')) {
      const [ipPart, rangePart] = input.split('/');

      if (!ipPart || !rangePart) {
        return false;
      }

      if (!/^\d+$/.test(rangePart)) {
        return false;
      }

      const range = parseInt(rangePart, 10);
      const addr = ipaddr.parse(ipPart);

      if (addr.kind() === 'ipv4') {
        const validIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ipPart);
        return validIPv4 && range >= 0 && range <= 32;
      }

      return range >= 0 && range <= 128;
    }

    if (!ipaddr.isValid(input)) {
      return false;
    }

    const addr = ipaddr.parse(input);

    if (addr.kind() === 'ipv4') {
      return /^\d{1,3}(\.\d{1,3}){3}$/.test(input);
    }

    return true;
  } catch {
    return false;
  }
}

type UploadType = 'ipRanges' | 'distributionList';

interface ValidationResult {
  line: number;
  value: string;
  isValid: boolean;
  error?: string;
}

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  type: UploadType;
  onApply: (values: string) => void;
  currentValue?: string;
}

export function BulkUploadDialog({
  open,
  onClose,
  type,
  onApply,
  currentValue = '',
}: BulkUploadDialogProps) {
  const [textContent, setTextContent] = useState(currentValue);

  const title = type === 'ipRanges' ? 'Bulk Upload IP Ranges' : 'Bulk Upload Distribution List';
  const description =
    type === 'ipRanges'
      ? 'Upload a text file or paste IP addresses/CIDR ranges (one per line)'
      : 'Upload a text file or paste email addresses (one per line)';

  const validateLine = useCallback(
    (line: string): boolean => {
      const trimmed = line.trim();
      if (!trimmed) {
        return true;
      }

      if (type === 'ipRanges') {
        return isStrictIPorCIDR(trimmed);
      }

      return EMAIL_REGEX.test(trimmed);
    },
    [type],
  );

  const validationResults = useMemo((): ValidationResult[] => {
    if (!textContent.trim()) {
      return [];
    }

    return textContent.split('\n').map((line, index) => {
      const trimmed = line.trim();
      const isValid = !trimmed || validateLine(trimmed);

      return {
        line: index + 1,
        value: line,
        isValid,
        error: isValid
          ? undefined
          : type === 'ipRanges'
          ? 'Invalid IP address or CIDR notation'
          : 'Invalid email address',
      };
    });
  }, [textContent, validateLine, type]);

  const invalidCount = validationResults.filter(r => !r.isValid).length;
  const validCount = validationResults.filter(r => r.isValid && r.value.trim()).length;

  const handleFilesDrop = useCallback(
    (acceptedFiles: File[], _fileRejections: FileRejection[], _event: DropEvent) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      const file = acceptedFiles[0];
      const reader = new FileReader();

      reader.onload = e => {
        const text = e.target?.result as string;
        setTextContent(text || '');
      };

      reader.readAsText(file);
    },
    [],
  );

  const handleApply = () => {
    if (invalidCount > 0) {
      return;
    }

    const cleanedContent = textContent
      .split('\n')
      .map(line => line.trim())
      .filter(line => line && validateLine(line))
      .join(', ');

    onApply(cleanedContent);
    onClose();
  };

  const handleClose = () => {
    setTextContent(currentValue);
    onClose();
  };

  const renderHighlightedTextarea = () => (
    <div className='relative'>
      <Textarea
        value={textContent}
        onChange={e => {
          setTextContent(e.target.value);
        }}
        placeholder={
          type === 'ipRanges'
            ? 'Enter IP addresses or CIDR ranges, one per line:\n192.168.1.1\n10.0.0.0/24\n2001:db8::1'
            : 'Enter email addresses, one per line:\nuser1@example.com\nuser2@example.com'
        }
        className='font-mono min-h-[300px] resize-none'
        spellCheck={false}
      />

      {validationResults.length > 0 && (
        <div className='mt-2 space-y-1 max-h-32 overflow-auto rounded border p-2 bg-muted/30'>
          {validationResults
            .filter(r => !r.isValid)
            .map(result => (
              <div key={result.line} className='text-xs text-destructive flex items-start gap-2'>
                <span className='font-mono font-semibold min-w-[3ch] text-right'>
                  {result.line}:
                </span>
                <span className='flex-1 break-all'>{result.value}</span>
                <span className='text-muted-foreground italic'>{result.error}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-2xl max-h-[90vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className='flex-1 overflow-auto space-y-4'>
          <Dropzone
            accept={{ 'text/plain': ['.txt'] }}
            maxFiles={1}
            onDrop={handleFilesDrop}
            className='w-full border-2 border-dashed hover:bg-muted/50'
          >
            <DropzoneEmptyState>
              <div className='flex flex-col items-center justify-center gap-2'>
                <FileTextIcon className='h-8 w-8 text-muted-foreground' />
                <p className='text-sm text-muted-foreground'>
                  Drop a .txt file here or click to browse
                </p>
              </div>
            </DropzoneEmptyState>
            <DropzoneContent />
          </Dropzone>

          <div className='relative'>
            <div className='absolute left-0 right-0 top-1/2 -translate-y-1/2 border-t' />
            <div className='relative flex justify-center'>
              <span className='bg-background px-2 text-xs text-muted-foreground'>OR</span>
            </div>
          </div>

          {renderHighlightedTextarea()}

          <div className='flex items-center justify-between text-sm'>
            <div className='space-x-4'>
              <span className='text-green-600'>Valid: {validCount}</span>
              {invalidCount > 0 && (
                <span className='text-destructive'>Invalid: {invalidCount}</span>
              )}
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                setTextContent('');
              }}
              disabled={!textContent}
            >
              Clear
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={invalidCount > 0 || !textContent.trim()}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
